import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Clock, Building2, Users, Shield, ChevronRight, 
  BarChart3, FileText, Calendar, AlertTriangle,
  CheckCircle2, Zap, Globe, ArrowRight, Loader2,
  Smartphone, Download, MapPin, Fingerprint, QrCode,
  Monitor, Scan, Wifi, FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// URLs de produção para os QR Codes (fixas para não depender do ambiente)
const PRODUCTION_DOMAIN = 'https://jornyx.com.br';
const APP_URL = PRODUCTION_DOMAIN + '/unified';
const EMPRESA_PONTO_URL = PRODUCTION_DOMAIN + '/empresa-ponto';

const LandingPage = () => {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState([]);
  const [loadingPlanos, setLoadingPlanos] = useState(true);

  useEffect(() => {
    fetchPlanos();
  }, []);

  const fetchPlanos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/planos/publico`);
      if (response.ok) {
        const data = await response.json();
        setPlanos(data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      // Usar planos padrão em caso de erro
      setPlanos(defaultPlans);
    } finally {
      setLoadingPlanos(false);
    }
  };

  // Planos padrão caso o backend não responda
  const defaultPlans = [
    {
      id: 'default-1',
      nome: 'Starter',
      valor_mensal: 99.90,
      descricao: 'Ideal para pequenas empresas',
      limite_empregados: 100,
      features: ['Ponto eletrônico', 'Gestão de escalas', 'Banco de horas', 'Suporte por email'],
      destaque: false
    },
    {
      id: 'default-2',
      nome: 'Business',
      valor_mensal: 179.90,
      descricao: 'Para empresas em crescimento',
      limite_empregados: 300,
      features: ['Ponto eletrônico', 'Gestão de escalas', 'Banco de horas', 'Suporte prioritário'],
      destaque: true
    },
    {
      id: 'default-3',
      nome: 'Enterprise',
      valor_mensal: 499.90,
      descricao: 'Solução completa',
      limite_empregados: 1000,
      features: ['Ponto eletrônico', 'Gestão de escalas', 'Banco de horas', 'Suporte dedicado'],
      destaque: false
    }
  ];

  // Usar planos do backend ou padrão
  const displayPlanos = planos.length > 0 ? planos : defaultPlans;

  const features = [
    {
      icon: Clock,
      title: 'Ponto Eletrônico',
      description: 'Registro de ponto via REP, catraca, reconhecimento facial ou aplicativo mobile.'
    },
    {
      icon: Calendar,
      title: 'Gestão de Escalas',
      description: 'Suporte a escalas 5x2, 6x1, 12x36 e turnos rotativos com configuração flexível.'
    },
    {
      icon: BarChart3,
      title: 'Banco de Horas',
      description: 'Controle automático de créditos, débitos, compensações e vencimentos.'
    },
    {
      icon: FileText,
      title: 'Atestados Médicos',
      description: 'Gestão completa de atestados com análise de CID e controle INSS.'
    },
    {
      icon: AlertTriangle,
      title: 'Monitor de Riscos',
      description: 'Alertas automáticos de passivo trabalhista e irregularidades.'
    },
    {
      icon: Shield,
      title: 'Análise NTEP',
      description: 'Identificação automática de nexo técnico epidemiológico com relatórios de impugnação.'
    }
  ];

  // Módulos adicionais
  const additionalModules = [
    { name: 'Gestão de atestados', icon: FileText },
    { name: 'Monitor de riscos', icon: AlertTriangle },
    { name: 'Análise NTEP com IA', icon: Shield },
    { name: 'Epidemiologia avançada', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-primary font-['Outfit']">Jornyx</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Funcionalidades</a>
              <a href="#app" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">App</a>
              <a href="#empresas" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Para Empresas</a>
              <a href="#plans" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Planos</a>
              <a href="#about" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors">Sobre</a>
            </nav>

            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="header-login-btn"
              >
                Entrar
              </Button>
              <Button 
                className="bg-accent hover:bg-accent/90 text-white"
                onClick={() => navigate('/login')}
                data-testid="header-start-btn"
              >
                Começar agora
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Conforme Portaria MTP nº 671/2021
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary font-['Outfit'] leading-tight mb-6">
                Gestão de Jornada<br />
                <span className="text-accent">Inteligente</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                Plataforma SaaS completa para controle de ponto, gestão de escalas, banco de horas e análise epidemiológica ocupacional.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-white font-medium h-12 px-8"
                  onClick={() => navigate('/login')}
                  data-testid="hero-start-btn"
                >
                  Começar gratuitamente
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="h-12 px-8"
                  onClick={() => navigate('/login')}
                >
                  Ver demonstração
                </Button>
              </div>

              <div className="flex items-center gap-8 mt-10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                  <span className="text-sm text-slate-600">30 dias grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                  <span className="text-sm text-slate-600">Sem cartão de crédito</span>
                </div>
              </div>
            </div>

            <div className="relative animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-slate-100">
                <img 
                  src="https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwZGl2ZXJzZXxlbnwwfHx8fDE3NzM2NzExMDJ8MA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Equipe moderna trabalhando"
                  className="rounded-xl w-full h-auto object-cover"
                />
              </div>
              
              {/* Floating cards */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-lg p-4 border border-slate-100 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">Dezenas</p>
                    <p className="text-xs text-slate-500">Empresas Ativas</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-lg p-4 border border-slate-100 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-info/10 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-info" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">Centenas</p>
                    <p className="text-xs text-slate-500">Empregados gerenciados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary font-['Outfit'] mb-4">
              Tudo que você precisa
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Uma plataforma completa para gestão de jornada de trabalho com conformidade total à legislação brasileira.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 group"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-primary font-['Outfit'] mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Mobile Section */}
      <section id="app" className="py-20 px-4 md:px-8 lg:px-12 bg-gradient-to-br from-primary to-primary/90">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Smartphone className="w-4 h-4" />
                App para Funcionários
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-6">
                Bata ponto pelo celular,<br />
                <span className="text-accent">de qualquer lugar</span>
              </h2>
              
              <p className="text-lg text-slate-300 mb-8">
                O app Jornyx permite que seus funcionários registrem o ponto de forma rápida e segura, 
                com geolocalização e consulta de saldo em tempo real.
              </p>

              {/* App Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Fingerprint className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm font-medium">Registro rápido</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm font-medium">Geolocalização</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm font-medium">Saldo em tempo real</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm font-medium">100% seguro</span>
                </div>
              </div>

              {/* Download Buttons & QR Code */}
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex flex-col gap-4">
                  <Button 
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-white font-medium h-14 px-6"
                    onClick={() => navigate('/unified')}
                    data-testid="download-app-btn"
                  >
                    <Download className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="text-xs opacity-80">Acesse agora</div>
                      <div className="font-semibold">App Jornyx</div>
                    </div>
                  </Button>
                  
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 h-14 px-6"
                    onClick={() => {
                      window.open('/unified', '_blank');
                    }}
                    data-testid="install-pwa-btn"
                  >
                    <Smartphone className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="text-xs opacity-80">Abrir em nova aba</div>
                      <div className="font-semibold">Instalar no celular</div>
                    </div>
                  </Button>
                </div>

                {/* QR Code */}
                <div className="bg-white p-4 rounded-2xl shadow-xl" data-testid="qr-code-container">
                  <QRCodeSVG 
                    value={APP_URL}
                    size={140}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    level="M"
                    includeMargin={false}
                  />
                  <div className="text-center mt-2">
                    <p className="text-xs font-medium text-primary">Escaneie para acessar</p>
                    <p className="text-[10px] text-slate-500">Aponte a câmera do celular</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-400 mt-4">
                * Escaneie o QR Code ou clique no botão para acessar. No celular, você pode adicionar à tela inicial para acesso rápido.
              </p>
            </div>

            {/* Phone Mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Phone frame */}
                <div className="w-72 h-[580px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Status bar */}
                    <div className="h-12 bg-accent flex items-center justify-center">
                      <div className="w-20 h-5 bg-black/20 rounded-full"></div>
                    </div>
                    
                    {/* App content preview */}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-accent rounded-xl flex items-center justify-center">
                          <Clock className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Bem-vindo</p>
                          <p className="font-semibold text-primary">Maria Silva</p>
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                        <p className="text-xs text-slate-500 mb-1">Saldo atual</p>
                        <p className="text-2xl font-bold text-accent">+12:30</p>
                        <p className="text-xs text-slate-400">horas extras</p>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Entrada</span>
                          <span className="font-semibold text-primary">08:02</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Saída almoço</span>
                          <span className="font-semibold text-primary">12:00</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Retorno</span>
                          <span className="font-semibold text-primary">13:00</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Saída</span>
                          <span className="text-slate-400">--:--</span>
                        </div>
                      </div>

                      <button className="w-full bg-accent text-white py-4 rounded-2xl font-semibold text-lg shadow-lg shadow-accent/30">
                        Registrar Ponto
                      </button>
                    </div>
                  </div>
                </div>

                {/* Floating notification */}
                <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-xl p-3 animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-primary">Ponto registrado!</p>
                      <p className="text-[10px] text-slate-500">08:02 - Entrada</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Para Empresas Section - Totem/Tablet */}
      <section id="empresas" className="py-20 px-4 md:px-8 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-white order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Monitor className="w-4 h-4" />
                App para Totem / Tablet
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-6">
                Ponto com<br />
                <span className="text-emerald-400">Reconhecimento Facial</span>
              </h2>
              
              <p className="text-lg text-slate-300 mb-8">
                Configure um tablet ou totem na sua empresa para registro de ponto com reconhecimento facial. 
                Seguro, rápido e em conformidade com a Portaria MTP 671/2021 (REP-P).
              </p>

              {/* Empresa Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Scan className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium">Reconhecimento facial</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium">REP-P / REP-A</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <FileDown className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium">Exportar AFD</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Wifi className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium">100% na nuvem</span>
                </div>
              </div>

              {/* Download Buttons & QR Code */}
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex flex-col gap-4">
                  <Button 
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium h-14 px-6"
                    onClick={() => navigate('/empresa-ponto')}
                    data-testid="totem-app-btn"
                  >
                    <Monitor className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="text-xs opacity-80">Configurar agora</div>
                      <div className="font-semibold">App de Ponto</div>
                    </div>
                  </Button>
                  
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 h-14 px-6"
                    onClick={() => {
                      window.open('/empresa-ponto', '_blank');
                    }}
                    data-testid="totem-new-tab-btn"
                  >
                    <Building2 className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="text-xs opacity-80">Abrir em nova aba</div>
                      <div className="font-semibold">Configurar Totem</div>
                    </div>
                  </Button>
                </div>

                {/* QR Code */}
                <div className="bg-white p-4 rounded-2xl shadow-xl" data-testid="qr-code-empresa-container">
                  <QRCodeSVG 
                    value={EMPRESA_PONTO_URL}
                    size={140}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    level="M"
                    includeMargin={false}
                  />
                  <div className="text-center mt-2">
                    <p className="text-xs font-medium text-primary">Escaneie no tablet</p>
                    <p className="text-[10px] text-slate-500">Para configurar o totem</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-400 mt-4">
                * Ideal para tablets dedicados na recepção ou entrada da empresa. Configure uma vez e deixe funcionando.
              </p>
            </div>

            {/* Totem Mockup */}
            <div className="relative flex justify-center lg:justify-start order-1 lg:order-2">
              <div className="relative">
                {/* Tablet frame */}
                <div className="w-80 h-[480px] bg-slate-800 rounded-[2rem] p-4 shadow-2xl border border-slate-700">
                  <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 rounded-[1.5rem] overflow-hidden relative">
                    {/* Header */}
                    <div className="h-16 bg-slate-800/80 flex items-center justify-between px-4 border-b border-slate-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                          <Clock className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">JORNYX</p>
                          <p className="text-sm font-semibold text-white">Empresa XYZ</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-mono font-bold text-white">14:32</p>
                        <p className="text-[10px] text-slate-400">Quarta-feira</p>
                      </div>
                    </div>
                    
                    {/* Camera area */}
                    <div className="flex-1 flex items-center justify-center p-6">
                      <div className="relative w-52 h-64 bg-slate-700/50 rounded-2xl overflow-hidden">
                        {/* Face guide */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-36 h-48 rounded-[50%] border-4 border-emerald-400/50 animate-pulse"></div>
                        </div>
                        {/* Camera icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Scan className="w-16 h-16 text-emerald-400/30" />
                        </div>
                      </div>
                    </div>

                    {/* Status bar */}
                    <div className="h-20 bg-emerald-600 flex items-center justify-center px-4">
                      <p className="text-white font-medium text-center">
                        Posicione seu rosto na câmera
                      </p>
                    </div>
                  </div>
                </div>

                {/* Floating badge */}
                <div className="absolute -right-4 top-1/3 bg-emerald-500 rounded-xl shadow-xl p-3 animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-xs font-semibold text-white">Ponto registrado!</p>
                      <p className="text-[10px] text-emerald-100">Maria Silva</p>
                    </div>
                  </div>
                </div>

                {/* REP-P Badge */}
                <div className="absolute -left-4 bottom-1/4 bg-slate-800 rounded-lg shadow-xl px-3 py-2 border border-emerald-500/30">
                  <p className="text-[10px] text-emerald-400 font-medium">Portaria 671/2021</p>
                  <p className="text-xs font-bold text-white">REP-P Homologado</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-20 px-4 md:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary font-['Outfit'] mb-4">
              Planos para cada necessidade
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Escolha o plano ideal para sua empresa. Todos incluem suporte e atualizações.
            </p>
          </div>

          {loadingPlanos ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : (
            <div className={`grid gap-8 mb-16 ${displayPlanos.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : displayPlanos.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'}`}>
              {displayPlanos.map((plan, index) => (
                <div 
                  key={plan.id || index}
                  className={`bg-white p-8 rounded-2xl border-2 transition-transform duration-200 hover:-translate-y-1 ${
                    plan.destaque 
                      ? 'border-accent shadow-xl scale-105' 
                      : 'border-slate-100 shadow-sm'
                  }`}
                >
                  {plan.destaque && (
                    <div className="bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                      Mais popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-primary font-['Outfit'] mb-2">{plan.nome}</h3>
                  <p className="text-slate-600 mb-4">{plan.descricao}</p>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-primary">
                      R$ {plan.valor_mensal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-slate-500">/mês</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">Até {plan.limite_empregados} empregados</p>
                  <ul className="space-y-3 mb-8">
                    {(plan.features || []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full h-11 ${
                      plan.destaque 
                        ? 'bg-accent hover:bg-accent/90 text-white' 
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                    onClick={() => navigate('/login')}
                  >
                    Começar teste grátis
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Módulos Adicionais */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-xl font-bold text-primary font-['Outfit'] mb-2">
                  Módulos Avançados
                </h3>
                <p className="text-slate-600">
                  Adicione funcionalidades especializadas ao seu plano
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {additionalModules.map((mod, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                      <mod.icon className="w-4 h-4 text-accent" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{mod.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-600 text-center">
                <span className="font-semibold text-primary">Entre em contato para negociar</span> após cadastro da sua empresa
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
            Pronto para modernizar sua gestão de jornada?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Comece hoje mesmo com 30 dias grátis. Sem compromisso.
          </p>
          <Button 
            size="lg"
            className="bg-accent hover:bg-accent/90 text-white font-medium h-12 px-8"
            onClick={() => navigate('/login')}
            data-testid="cta-start-btn"
          >
            Criar conta gratuita
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className="py-12 px-4 md:px-8 lg:px-12 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white font-['Outfit']">Jornyx</span>
              </div>
              <p className="text-slate-400 text-sm">
                Plataforma SaaS de gestão de jornada de trabalho com conformidade total à legislação brasileira.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Produto</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#plans" className="hover:text-white transition-colors">Planos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrações</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Sobre</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carreiras</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-white transition-colors">LGPD</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © 2025 Jornyx. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
